import { useMemo, useState } from "react";
import classNames from "classnames";
import { View, ViewDensity, ViewType } from "@common/types/views/views";
import { Task } from "@common/types/task";
import { FinancialDocumentColumn } from "@common/types/views/financialDocumentViews";
import {
    FinancialDocumentExtended,
    SpreadsheetImporterStatus,
} from "@common/types/financialDocument";
import { Entity } from "@common/types/entity";
import { financialDocumentsViewConfig } from "@components/documents/FinancialDocumentsView/financialDocumentsViewConfig";
import { FinancialDocumentUploadProvider } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadProvider";
import { FinancialDocumentUploadDropzone } from "@components/documents/FinancialDocumentUpload/FinancialDocumentUploadDropzone";
import { useFinancialDocumentUpload } from "@components/documents/FinancialDocumentUpload/useFinancialDocumentUpload";
import { DocumentsEmptyState } from "@components/documents/DocumentsEmptyState";
import { AddDocument } from "@components/documents/FinancialDocumentList/AddDocument";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { Button } from "@design-system/Button/Button";
import { Shortcut } from "@design-system/Shortcut/Shortcut";
import { useKeyboardCommand } from "@hooks/keyboard/useKeyboardCommand";
import { UploadIcon } from "@icons";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useEntities } from "@hooks/useEntities";
import { useTaskDocumentUpload } from "../../useTaskDocumentUpload";
import { GetDocumentsBulkActions } from "./GetDocumentsBulkActions";
import { useGetDocumentsBulkSelection } from "./useGetDocumentsBulkSelection";
import { useDocumentRowHighlight } from "../../useDocumentRowHighlight";
import { useLoadedRowIdResolver } from "../../useRefRowHighlight";
import styles from "./GetDocumentsTaskBody.module.scss";

type TaskDocument = NonNullable<Task["financialDocuments"]>[number];

/**
 * Converts a minimal task document into a FinancialDocumentExtended shape
 * required by the shared financialDocumentsViewConfig. Fields not available
 * on the task document are given safe defaults.
 */
function toFinancialDocumentExtended(
    doc: TaskDocument,
    taskEntity: Entity | null,
): FinancialDocumentExtended {
    const category = doc.documentCategory
        ? { ...doc.documentCategory, description: null }
        : null;

    return {
        ...doc,
        date: null,
        amount: null,
        originalFileName: null,
        hasAttemptedMatching: false,
        ocrSuccessful: false,
        isSpreadsheet: null,
        numTransactions: null,
        importerErrors: null,
        dismissedImporterErrors: null,
        transactionsImportStatus: null,
        spreadsheetImporterStatus: SpreadsheetImporterStatus.NOT_IMPORTED,
        csvFormatStr: null,
        spreadsheetParsingRuleId: null,
        entityId: null,
        financialAccountId: null,
        integrationAccountId: null,
        accountInfoExtracted: false,
        loanExtraction: null,
        loanExtracted: false,
        openingBalance: null,
        closingBalance: null,
        openingDate: null,
        closingDate: null,
        transactionsSpreadsheetId: null,
        externalExtendFileId: null,
        externalExtendLightParseFileId: null,
        importerQueue: null,
        documentCategoryId: doc.documentCategoryId ?? null,
        counterpartyId: null,
        cashAppExcludedLinkedAccountPaymentCount: null,
        entity: taskEntity
            ? { ...taskEntity, user: taskEntity.user ?? null }
            : null,
        documentCategory: category,
        counterparty: null,
        financialAccount: null,
        transactionMatches: [],
        integrationAccount: null,
        uploader: null,
        accountInfoExtraction: null,
        balanceSheetExtraction: null,
        coaExtraction: null,
        glExtraction: null,
        documentInvoicesExtraction: null,
        documentBillsExtraction: null,
        linkedJournalEntries: [],
        linkedInvoices: [],
        linkedBills: [],
    };
}

const DEFAULT_VIEW: View = {
    type: ViewType.FINANCIAL_DOCUMENT,
    columns: [
        { id: FinancialDocumentColumn.NAME },
        { id: FinancialDocumentColumn.DOCUMENT_CATEGORY },
        { id: FinancialDocumentColumn.PERIOD },
        { id: FinancialDocumentColumn.ENTITY },
    ],
    filters: {},
    options: {
        density: ViewDensity.MD,
        bulkActions: true,
        selection: true,
    },
};

interface InnerProps {
    selectedTask: Task;
    attachedDocumentIds: number[];
    attachDocument: (documentId: number) => void;
    attachDocuments: (documentIds: number[]) => void;
    detachDocuments: (documentIds: number[]) => Promise<boolean>;
}

const GetDocumentsTaskBodyInner = ({
    selectedTask,
    attachedDocumentIds,
    attachDocument,
    attachDocuments,
    detachDocuments,
}: Readonly<InnerProps>) => {
    const [view, setView] = useState<View>(DEFAULT_VIEW);
    const entities = useEntities();
    const { open } = useFinancialDocumentUpload();

    useKeyboardCommand({
        keys: "mod+enter",
        callback: open,
        options: {
            description: "Upload documents",
            preventDefault: true,
        },
    });

    const rows = useMemo(() => {
        const entityById = new Map(entities.map((e) => [e.id, e]));
        const firstEntityId = selectedTask.entityIds?.[0];
        const taskEntity =
            firstEntityId != null
                ? (entityById.get(firstEntityId) ?? null)
                : null;
        return (selectedTask.financialDocuments ?? []).map((doc) =>
            toFinancialDocumentExtended(doc, taskEntity),
        );
    }, [selectedTask.financialDocuments, selectedTask.entityIds, entities]);

    const {
        rowSelection,
        setRowSelection,
        selectedDocumentIds,
        hasBulkSelection,
        isRemoving,
        canAskAbout,
        handleClearBulkSelection,
        handleBulkRemoveFromTask,
        handleBulkAskAbout,
        rowDragOut,
    } = useGetDocumentsBulkSelection({ rows, detachDocuments });

    const resolveTableRowId = useLoadedRowIdResolver(rows);
    const { viewTableRef, selectedRowId } = useDocumentRowHighlight({
        resolveTableRowId,
    });

    return (
        <FinancialDocumentUploadDropzone
            dropzoneClassName={styles.dropzone}
            cover={
                <div className={styles.dropCover}>
                    <UploadIcon />
                    <span>Drop files to upload</span>
                </div>
            }
        >
            <ViewTable
                ref={viewTableRef}
                data={rows}
                view={view}
                config={financialDocumentsViewConfig}
                selectedRowId={selectedRowId}
                fixedTopOffsetPx
                hideTopBorder
                // The app header hides via a `body` class when
                // fullscreen, which only shifts this table's position,
                // not its own size — the auto top-offset measurement's
                // ResizeObserver alone wouldn't catch it, so force a
                // re-measure on the flag itself.
                onViewChange={setView}
                rowSelection={rowSelection}
                onRowSelectionChange={setRowSelection}
                rowDragOut={rowDragOut}
                emptyView={() => (
                    <DocumentsEmptyState header="Upload or drag and drop your files">
                        <Button
                            variant="primary"
                            size="md"
                            onClick={open}
                            leftIcon={<UploadIcon />}
                        >
                            Upload
                        </Button>
                    </DocumentsEmptyState>
                )}
                outerBorder={false}
                enableColumnResizing
                className={styles.table}
                footer={
                    hasBulkSelection ? (
                        <GetDocumentsBulkActions
                            selectedCount={selectedDocumentIds.length}
                            canRemoveFromTask={selectedDocumentIds.length > 0}
                            canAskAbout={canAskAbout}
                            isRemoving={isRemoving}
                            onClearSelection={handleClearBulkSelection}
                            onRemoveFromTask={() => {
                                void handleBulkRemoveFromTask();
                            }}
                            onAskAbout={handleBulkAskAbout}
                        />
                    ) : (
                        <CommandIsland
                            sticky
                            className={classNames(
                                commandIslandStyles.bulkActionsStickyFooter,
                                styles.commandIsland,
                            )}
                        >
                            <AddDocument
                                attachedDocumentIds={attachedDocumentIds}
                                attachDocument={attachDocument}
                                attachDocuments={attachDocuments}
                                trigger={
                                    <Button
                                        variant="primary"
                                        size="md"
                                        leftIcon={<UploadIcon />}
                                        rightIcon={
                                            <span data-slot-box="">
                                                <Shortcut keys="mod+enter" />
                                            </span>
                                        }
                                    >
                                        Upload documents
                                    </Button>
                                }
                            />
                        </CommandIsland>
                    )
                }
            />
        </FinancialDocumentUploadDropzone>
    );
};

interface Props {
    selectedTask: Task;
}

export const GetDocumentsTaskBody = ({ selectedTask }: Readonly<Props>) => {
    const {
        handleUploadCompleted,
        attachDocument,
        attachDocuments,
        attachedDocumentIds,
        detachDocuments,
    } = useTaskDocumentUpload(selectedTask);

    return (
        <FinancialDocumentUploadProvider onCompleted={handleUploadCompleted}>
            <GetDocumentsTaskBodyInner
                selectedTask={selectedTask}
                attachedDocumentIds={attachedDocumentIds}
                attachDocument={attachDocument}
                attachDocuments={attachDocuments}
                detachDocuments={detachDocuments}
            />
        </FinancialDocumentUploadProvider>
    );
};
